/**
 * The one place the SPA speaks to the bridge.
 *
 * Every call is a same-origin `POST` carrying the PROTOCOL.md envelope; every
 * failure is normalised to a {@link BridgeError} so the UI maps error codes
 * to copy in exactly one place. Retries exist for transient transport
 * failures on read endpoints only — a send is the user's words going out of
 * the door and is never silently replayed.
 */
import type { BridgeErrorCode, BridgeErrorBody } from "./types.ts";

/**
 * Default per-attempt deadline. The Worker's own I/O deadline is 15 s and a
 * folder listing can legitimately hold dozens of `STATUS` round-trips, so
 * the client budget sits comfortably above it.
 */
const DEFAULT_TIMEOUT_MS = 30_000;

/**
 * Attempts per call (1 initial + this many retries) for transient failures.
 */
const DEFAULT_RETRIES = 2;

/**
 * Base of the exponential backoff between attempts.
 */
const RETRY_DELAY_MS = 500;

/**
 * Transport-level statuses worth another attempt: the connection to the
 * Worker or from the Worker to the mail host may simply have hiccuped.
 */
const RETRYABLE_STATUS = new Set([429, 500, 502, 503, 504]);

/**
 * A failed bridge call, typed by protocol error code.
 *
 * `message` is always safe to show: the bridge never forwards internal
 * error text, and this class never echoes lower-level causes into it.
 */
export class BridgeError extends Error {
  /**
  Protocol error code (PROTOCOL.md §4).
   */
  readonly code: BridgeErrorCode;

  /**
  HTTP status the failure arrived with (`0` for transport failures).
   */
  readonly status: number;

  /**
  Builds a protocol failure carrying its HTTP context.
   *
   * @param code - Protocol error code.
   * @param message - User-safe sentence.
   * @param status - HTTP status, `0` when the request never got a response.
   */
  constructor(code: BridgeErrorCode, message: string, status = 0) {
    super(message);
    this.name = "BridgeError";
    this.code = code;
    this.status = status;
  }

  /**
   * True when the failure is worth another automatic attempt.
   *
   * @returns Whether a retry may plausibly succeed.
   */
  get isTransient(): boolean {
    return (
      this.status === 0 ||
      RETRYABLE_STATUS.has(this.status) ||
      this.code === "timeout"
    );
  }
}

/**
 * Options for {@link bridgeCall}.
 */
export interface BridgeCallOptions {
  /**
  Per-attempt deadline in milliseconds.
   */
  readonly timeoutMs?: number;
  /**
  Extra attempts after the first on transient failures.
   */
  readonly retries?: number;
  /**
  Backoff base in milliseconds.
   */
  readonly retryDelayMs?: number;
  /**
  False for non-idempotent calls (`smtp/send`): no automatic replay.
   */
  readonly idempotent?: boolean;
  /**
  Abort signal from the caller (component unmount, view switch).
   */
  readonly signal?: AbortSignal;
}

/**
 * Result of one fetch attempt, before retry policy applies.
 */
type Attempt<T> =
  | { readonly kind: "data"; readonly data: T }
  | {
      readonly kind: "error";
      readonly error: BridgeError;
    };

/**
 * Performs one bridge call and unwraps the envelope.
 *
 * @param path - API path beginning `/api/`.
 * @param payload - Request body; serialised as JSON.
 * @param options - Timeouts, retries, idempotence, caller signal.
 * @returns The envelope's `data` member.
 * @throws {BridgeError} When every attempt fails, with the last failure.
 */
export async function bridgeCall<T>(
  path: string,
  payload: unknown,
  options: BridgeCallOptions = {},
): Promise<T> {
  const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const retries = options.retries ?? DEFAULT_RETRIES;
  const retryDelayMs = options.retryDelayMs ?? RETRY_DELAY_MS;
  const isIdempotent = options.idempotent ?? true;

  let lastError: BridgeError | null = null;
  for (let attempt = 0; attempt <= retries; attempt += 1) {
    if (attempt > 0) {
      await delay(backoff(retryDelayMs, attempt), options.signal);
    }
    const result = await attemptOnce<T>(
      path,
      payload,
      timeoutMs,
      options.signal,
    );
    if (result.kind === "data") {
      return result.data;
    }
    lastError = result.error;
    if (!isIdempotent || !lastError.isTransient) {
      throw lastError;
    }
  }
  throw lastError ?? new BridgeError("internal", "The request failed.", 0);
}

/**
 * Runs a single attempt: fetch, envelope parse, error normalise.
 *
 * @param path - API path.
 * @param payload - Request body.
 * @param timeoutMs - Per-attempt deadline.
 * @param outer - Caller's abort signal, combined with the deadline.
 * @returns The unwrapped data or a normalised failure.
 */
async function attemptOnce<T>(
  path: string,
  payload: unknown,
  timeoutMs: number,
  outer: AbortSignal | undefined,
): Promise<Attempt<T>> {
  const deadline = new AbortController();
  const timer = setTimeout(() => {
    deadline.abort();
  }, timeoutMs);
  const onOuterAbort = (): void => {
    deadline.abort();
  };
  outer?.addEventListener("abort", onOuterAbort, { once: true });

  try {
    const response = await fetch(path, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(payload),
      signal: deadline.signal,
      // Credentials are in the body; cookies would be noise (and the
      // deployment sets no cookies). Same-origin default sends none anyway,
      // spelled out so the security review does not have to wonder.
      credentials: "omit",
      referrerPolicy: "no-referrer",
    });
    return await readEnvelope<T>(response);
  } catch (error: unknown) {
    return {
      kind: "error",
      error: normaliseTransport(error, timeoutMs),
    };
  } finally {
    clearTimeout(timer);
    outer?.removeEventListener("abort", onOuterAbort);
  }
}

/**
 * Parses an API response into data or a typed failure.
 *
 * @param response - The fetch response.
 * @returns Unwrapped data or normalised failure.
 */
async function readEnvelope<T>(response: Response): Promise<Attempt<T>> {
  let body: unknown;
  try {
    body = await response.json();
  } catch {
    return {
      kind: "error",
      error: new BridgeError(
        "internal",
        "The server sent a response this client cannot read.",
        response.status,
      ),
    };
  }
  if (isEnvelope(body)) {
    if (body.ok) {
      return { kind: "data", data: body.data as T };
    }
    return {
      kind: "error",
      error: new BridgeError(
        body.error.code,
        body.error.message,
        response.status,
      ),
    };
  }
  // A gateway or platform error page — no envelope, still a real failure.
  return {
    kind: "error",
    error: new BridgeError(
      "internal",
      "The server answered outside the bridge protocol.",
      response.status,
    ),
  };
}

/**
 * Maps fetch rejections to a typed failure. Timeouts surface as `timeout`
 * so the UI can say "took too long" instead of "no connection".
 *
 * @param error - The thrown value from `fetch`.
 * @param timeoutMs - The deadline that was armed for the attempt.
 * @returns The normalised failure.
 */
function normaliseTransport(error: unknown, timeoutMs: number): BridgeError {
  if (error instanceof DOMException && error.name === "AbortError") {
    return new BridgeError("timeout", "The server took too long to answer.");
  }
  return new BridgeError(
    "connect_failed",
    `Could not reach the mail bridge (waited ${Math.round(timeoutMs / 1000)} s).`,
  );
}

/**
 * Structural check of the protocol envelope.
 *
 * @param body - Parsed JSON body.
 * @returns Whether the body has the documented envelope shape.
 */
function isEnvelope(
  body: unknown,
): body is
  | { readonly ok: true; readonly data: unknown }
  | { readonly ok: false; readonly error: BridgeErrorBody } {
  if (typeof body !== "object" || body === null || !("ok" in body)) {
    return false;
  }
  const envelope = body as { ok: unknown; data?: unknown; error?: unknown };
  if (envelope.ok === true) {
    return true;
  }
  if (
    envelope.ok !== false ||
    typeof envelope.error !== "object" ||
    envelope.error === null
  ) {
    return false;
  }
  const failure = envelope.error as { code?: unknown; message?: unknown };
  return (
    typeof failure.code === "string" && typeof failure.message === "string"
  );
}

/**
 * Exponential backoff with a hard ceiling.
 *
 * @param base - First-retry delay.
 * @param attempt - One-based attempt number.
 * @returns Milliseconds to wait.
 */
function backoff(base: number, attempt: number): number {
  const wait = base * 2 ** (attempt - 1);
  return Math.min(wait, 4000);
}

/**
 * Sleeps, aborting early with the caller's signal.
 *
 * @param ms - Milliseconds to wait.
 * @param signal - Caller's abort signal; aborting skips the wait.
 */
async function delay(
  ms: number,
  signal: AbortSignal | undefined,
): Promise<void> {
  if (signal?.aborted === true) {
    return;
  }
  await new Promise<void>((resolve) => {
    const timer = setTimeout(resolve, ms);
    signal?.addEventListener(
      "abort",
      () => {
        clearTimeout(timer);
        resolve();
      },
      { once: true },
    );
  });
}
